/**
 * Confirmation dialog for cutting the power to a selection of virtual machines at once.
 *
 * Deliberately light: no typed phrase, and the reason is optional. A stop is reversible with one
 * click — asking an operator to type a word there would only train them to type it.
 * What is kept is the selection listed in full, name, tenant and current status, because the one
 * mistake a stop can still make is landing on the wrong customer's machine.
 *
 * The backend refuses machines one at a time, so this dialog has a second life after the request:
 * when part of the batch was refused it stays open and shows which rows and why — for a provider
 * refusal that "why" is the hypervisor's own sentence, which is the only thing that says what to do
 * next.
 */

import { useEffect, useId, useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  STOP_REASON_MAX_LENGTH,
  type BulkStopResourcesResult,
  type Resource,
} from "@/types/admin";

interface BulkStopResourcesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The machines the operator picked, in the order the table shows them. */
  resources: Resource[];
  isPending: boolean;
  /**
   * The report of the last attempt, or null before one has been made. Non-null with failures is
   * what turns this dialog into a result view.
   */
  result: BulkStopResourcesResult | null;
  /** Handed the note the operator typed, already trimmed. Empty string when they typed nothing. */
  onConfirm: (reason: string) => void;
}

export function BulkStopResourcesDialog({
  open,
  onOpenChange,
  resources,
  isPending,
  result,
  onConfirm,
}: BulkStopResourcesDialogProps) {
  const reasonFieldId = useId();
  const [reason, setReason] = useState("");

  // A fresh open starts empty, so one batch's note can never be submitted against another
  // selection. A failed attempt keeps what was typed — the dialog stays open on top of the report.
  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  const canStop = !isPending && resources.length > 0;

  const failures = result?.results.filter((row) => !row.success) ?? [];
  const showingResult = failures.length > 0;

  const handleConfirm = () => {
    if (!canStop) {
      return;
    }

    onConfirm(reason.trim());
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Cancel is disabled while the request runs, so closing must be blocked too.
        if (isPending && !next) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        className="max-w-lg"
        onEscapeKeyDown={(event) => {
          if (isPending) {
            event.preventDefault();
          }
        }}
      >
        {showingResult ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {result!.successCount > 0
                  ? `${result!.successCount} of ${result!.totalRequested} resources stopped`
                  : "No resources were stopped"}
              </AlertDialogTitle>
            </AlertDialogHeader>

            <AlertDialogBody className="space-y-4">
              <AlertDialogDescription>
                {`${failures.length} resource${failures.length === 1 ? " was" : "s were"} refused and left as they were. `}
                They are still selected, so they can be fixed and stopped again.
              </AlertDialogDescription>
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-2">
                {failures.map((row) => (
                  <div key={row.resourceId} className="space-y-0.5">
                    <p className="truncate text-sm font-medium">
                      {row.resourceName || row.resourceId}
                    </p>
                    {/* The provider's own words, not a rewrite: "VM is not running" is what tells an
                      operator this row needs nothing done to it. */}
                    <p className="text-xs text-destructive-ink">
                      {row.errorMessage ?? row.errorCode ?? "Stop failed."}
                    </p>
                  </div>
                ))}
              </div>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {`Stop ${resources.length} selected virtual machine${resources.length === 1 ? "" : "s"}?`}
              </AlertDialogTitle>
            </AlertDialogHeader>

            <AlertDialogBody className="space-y-4">
              <AlertDialogDescription>
                The power is cut without telling the guest operating systems —
                the same as pulling the plug. Anything unsaved inside them is
                lost. They can be started again afterwards.
              </AlertDialogDescription>
              {/* The whole selection, not a count: an operator acts across tenants and a number alone
                cannot show that one of the rows belongs to the wrong customer. */}
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {resources.map((resource) => (
                  <div
                    key={resource.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {resource.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {resource.tenantName}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="shrink-0 px-1.5 py-0 text-[11px]"
                    >
                      {resource.status}
                    </Badge>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor={reasonFieldId}>Reason (optional)</Label>
                <Textarea
                  id={reasonFieldId}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why are these machines being stopped?"
                  maxLength={STOP_REASON_MAX_LENGTH}
                  rows={2}
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">
                  {`Up to ${STOP_REASON_MAX_LENGTH} characters. Recorded on every machine's audit log.`}
                </p>
              </div>

              <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-ink" />
                <span>
                  Each machine is stopped on its own. One the hypervisor refuses
                  — a VM that is not running, or one it has locked — is left as
                  it is, the rest still stop, and you will be told which were
                  refused.
                </span>
              </p>
            </AlertDialogBody>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
              {/* A plain Button, not AlertDialogAction: the action closes the dialog on click, and
                  this one must stay open while the request is in flight and when part of the batch
                  comes back refused. */}
              <Button onClick={handleConfirm} disabled={!canStop}>
                {isPending && <Spinner className="mr-2" />}
                {isPending
                  ? "Stopping..."
                  : `Stop ${resources.length} machine${resources.length === 1 ? "" : "s"}`}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
